import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import {
  AlertTriangle, Shield, Copy, Check, KeyRound, Mail, UserCheck,
  ShieldAlert, Archive, Loader2, RotateCcw, Trash2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
import AccountRecoveryChooseStep from './AccountRecoveryChooseStep';

const ROLE_COLORS = {
  super_admin: 'bg-rose-100 text-rose-800 border-rose-300',
  admin: 'bg-amber-100 text-amber-800 border-amber-300',
  cooper: 'bg-blue-100 text-blue-800 border-blue-300',
  expert: 'bg-violet-100 text-violet-800 border-violet-300',
  membre: 'bg-emerald-100 text-emerald-800 border-emerald-300',
};

/**
 * Modale "Libérer / reconstruire l'accès email" (super_admin uniquement).
 *
 * Flow :
 *  1. Saisir un email → preview des comptes (liste lisible avec assets_count)
 *  2. Choisir un compte principal + cases à cocher pour archiver les autres
 *  3. Choisir mode (magic_link par défaut, ou temp_password)
 *  4. Renseigner une raison (>= 10 caractères)
 *  5. Confirmer explicitement
 *  6. Résultat affiché avec magic_link ou temp_password copiable
 */
export default function AdminAccountRecoveryModal({ open, onClose, initialEmail = '' }) {
  const [step, setStep] = useState('lookup'); // lookup | choose | result
  const [email, setEmail] = useState(initialEmail);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [targetUserId, setTargetUserId] = useState('');
  const [archiveIds, setArchiveIds] = useState([]);
  const [mode, setMode] = useState('magic_link');
  const [reason, setReason] = useState('');
  const [notifyMember, setNotifyMember] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(null);
  // Renvoi par email depuis l'étape résultat
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resentTo, setResentTo] = useState(null);
  // V36 — Mémo de la dernière raison utilisée (localStorage)
  const LAST_REASON_KEY = 'osc.recovery.lastReason';
  const [lastReason, setLastReason] = useState(() => {
    try {
      const stored = localStorage.getItem(LAST_REASON_KEY);
      // Seed initial : si vide, on injecte la dernière raison communiquée
      // par le super-admin pour qu'elle soit directement disponible.
      if (!stored) {
        const seed = 'Changement de statut';
        localStorage.setItem(LAST_REASON_KEY, seed);
        return seed;
      }
      return stored;
    } catch { return ''; }
  });

  useEffect(() => {
    if (open) {
      setStep('lookup');
      setEmail(initialEmail);
      setAccounts([]);
      setTargetUserId('');
      setArchiveIds([]);
      setMode('magic_link');
      setReason('');
      setNotifyMember(true);
      setConfirmed(false);
      setResult(null);
      setCopied(null);
      setResendEmail('');
      setResentTo(null);
      // Recharge la dernière raison depuis localStorage à chaque ouverture
      try { setLastReason(localStorage.getItem(LAST_REASON_KEY) || ''); } catch { /* noop */ }
    }
  }, [open, initialEmail]);

  // Notify par défaut suivant le mode
  useEffect(() => {
    setNotifyMember(mode === 'magic_link');
  }, [mode]);

  const handleLookup = async () => {
    if (!email.trim() || !email.includes('@')) {
      toast.error('Email invalide');
      return;
    }
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const res = await axios.post(
        `${API}/api/admin/account-recovery/preview`,
        { email: email.trim().toLowerCase() },
        { headers },
      );
      const items = res.data?.accounts || [];
      setAccounts(items);
      if (items.length === 0) {
        toast.error('Aucun compte trouvé pour cet email');
      } else {
        // Présélectionne le 1er non super_admin
        const candidate = items.find((a) => !a.is_super_admin) || items[0];
        setTargetUserId(candidate.user_id);
        setStep('choose');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  const toggleArchive = (uid) => {
    setArchiveIds((prev) => (prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]));
  };

  const handleExecute = async () => {
    if (!confirmed) {
      toast.error('Cochez la confirmation explicite pour continuer');
      return;
    }
    if (reason.trim().length < 10) {
      toast.error('Raison trop courte (10 caractères min.)');
      return;
    }
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const res = await axios.post(
        `${API}/api/admin/account-recovery/execute`,
        {
          email: email.trim().toLowerCase(),
          target_user_id: targetUserId,
          mode,
          reason: reason.trim(),
          confirmed: true,
          notify_member: notifyMember,
          archive_duplicate_user_ids: archiveIds,
        },
        { headers },
      );
      setResult(res.data);
      // Pré-remplit le champ resend avec l'email du compte
      setResendEmail(res.data.email || email.trim().toLowerCase());
      setStep('result');
      // V36 — Mémorise la raison pour la prochaine ouverture
      try {
        localStorage.setItem(LAST_REASON_KEY, reason.trim());
        setLastReason(reason.trim());
      } catch { /* localStorage indisponible — pas grave */ }
      toast.success('Accès reconstruit avec succès');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la reconstruction');
    } finally {
      setLoading(false);
    }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      toast.success('Copié dans le presse-papiers');
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const handleResend = async () => {
    if (!resendEmail.includes('@')) {
      toast.error('Email destinataire invalide');
      return;
    }
    setResending(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.post(
        `${API}/api/admin/account-recovery/resend-email`,
        {
          target_user_id: result.target_user_id,
          to_email: resendEmail.trim().toLowerCase(),
          magic_link: result.magic_link || null,
          temp_password: result.temp_password || null,
        },
        { headers },
      );
      setResentTo(resendEmail.trim().toLowerCase());
      toast.success(`Email envoyé à ${resendEmail.trim().toLowerCase()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setResending(false);
    }
  };

  const target = accounts.find((a) => a.user_id === targetUserId);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="account-recovery-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <ShieldAlert className="w-5 h-5 text-rose-600" />
            Libérer / reconstruire l'accès email
          </DialogTitle>
          <p className="text-sm text-slate-500">
            Action sensible — réservée au super administrateur. Toutes les données métier sont conservées.
          </p>
        </DialogHeader>

        {/* ---------- ÉTAPE 1 : LOOKUP ---------- */}
        {step === 'lookup' && (
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="rec-email">Email à reconstruire</Label>
              <Input
                id="rec-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="utilisateur@exemple.com"
                className="mt-1"
                data-testid="recovery-email-input"
                autoFocus
              />
              <p className="text-xs text-slate-500 mt-1">
                Le système cherche le compte exact et les éventuels archivés (recovery-archive-*).
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-900">
                Cette action est <strong>journalisée</strong> (audit). Aucun super_admin ne peut être reconstruit.
              </p>
            </div>
          </div>
        )}

        {/* ---------- ÉTAPE 2 : CHOOSE ---------- */}
        {step === 'choose' && (
          <AccountRecoveryChooseStep
            accounts={accounts}
            archiveIds={archiveIds}
            toggleArchive={toggleArchive}
            targetUserId={targetUserId}
            setTargetUserId={setTargetUserId}
            mode={mode}
            setMode={setMode}
            reason={reason}
            setReason={setReason}
            notifyMember={notifyMember}
            setNotifyMember={setNotifyMember}
            confirmed={confirmed}
            setConfirmed={setConfirmed}
            lastReason={lastReason}
            email={email}
            ROLE_COLORS={ROLE_COLORS}
          />
        )}

        {/* ---------- ÉTAPE 3 : RESULT ---------- */}
        {step === 'result' && result && (
          <div className="space-y-4 py-2" data-testid="recovery-result">
            {result.mode === 'release_email' ? (
              <div className="bg-rose-50 border border-rose-300 rounded-md p-4 flex items-start gap-3">
                <Trash2 className="w-5 h-5 text-rose-600 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-rose-900">Email libéré avec succès</div>
                  <div className="text-sm text-rose-700 mt-1">
                    <strong>{result.email_released}</strong> est désormais disponible pour une nouvelle inscription.
                  </div>
                  <div className="text-xs text-slate-600 mt-2 space-y-0.5">
                    <div>• Email renommé en : <code className="text-[10px]">{result.released_email_renamed_to}</code></div>
                    <div>• {result.tokens_invalidated} token(s) invalidé(s)</div>
                    {result.archived?.length > 0 && <div>• {result.archived.length} compte(s) archivé(s)</div>}
                    {result.notified && <div>• Email de confirmation envoyé</div>}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 flex items-start gap-3">
                <UserCheck className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-emerald-900">Accès reconstruit</div>
                  <div className="text-sm text-emerald-700">
                    {result.email} · {result.tokens_invalidated} token(s) invalidé(s)
                    {result.archived?.length > 0 && ` · ${result.archived.length} compte(s) archivé(s)`}
                    {result.notified && ' · email envoyé'}
                  </div>
                </div>
              </div>
            )}

            {result.magic_link && (
              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">Magic-link à transmettre</Label>
                <div className="mt-1 flex gap-2">
                  <Input value={result.magic_link} readOnly className="font-mono text-xs" />
                  <Button onClick={() => copy(result.magic_link, 'link')} size="sm" variant="outline">
                    {copied === 'link' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
            )}

            {result.temp_password && (
              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">Mot de passe temporaire</Label>
                <div className="mt-1 flex gap-2">
                  <Input value={result.temp_password} readOnly className="font-mono text-sm" />
                  <Button onClick={() => copy(result.temp_password, 'pw')} size="sm" variant="outline">
                    {copied === 'pw' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                </div>
                <p className="text-xs text-amber-700 mt-1">
                  ⚠️ Transmettre hors-bande. L'utilisateur devra le changer à la 1re connexion.
                </p>
              </div>
            )}

            {/* ENVOI / RENVOI PAR EMAIL — masqué en mode release_email (compte libéré) */}
            {result.mode !== 'release_email' && (
            <div className="border-t border-slate-200 pt-4 mt-4">
              <Label className="text-xs font-semibold uppercase text-slate-500 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" />
                Envoyer / renvoyer par email
              </Label>
              <p className="text-xs text-slate-500 mt-1 mb-2">
                Transmettre {result.magic_link ? 'le magic-link' : 'le mot de passe temporaire'} à
                l'adresse du compte ou une autre adresse (secrétariat, perso, etc.).
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="destinataire@exemple.com"
                  className="flex-1"
                  data-testid="recovery-resend-email"
                />
                <Button
                  onClick={handleResend}
                  disabled={resending || !resendEmail.includes('@')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="recovery-resend-btn"
                >
                  {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  <span className="ml-1.5">Envoyer</span>
                </Button>
              </div>
              {resentTo && (
                <p className="text-xs text-emerald-700 mt-2 flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" /> Email envoyé à <strong>{resentTo}</strong>
                </p>
              )}
            </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'lookup' && (
            <>
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button onClick={handleLookup} disabled={loading || !email.includes('@')} data-testid="recovery-lookup-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Rechercher
              </Button>
            </>
          )}
          {step === 'choose' && (
            <div className="w-full flex flex-col gap-2">
              {/* Message d'aide quand le bouton est désactivé */}
              {(!confirmed || reason.trim().length < 10 || !targetUserId) && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="recovery-blockers">
                  <strong>Pour activer le bouton :</strong>
                  <ul className="list-disc list-inside mt-0.5 space-y-0.5">
                    {!targetUserId && <li>Sélectionnez un compte principal à conserver</li>}
                    {reason.trim().length < 10 && <li>Saisissez une raison de min. 10 caractères ({reason.trim().length}/10)</li>}
                    {!confirmed && <li>Cochez la case de confirmation d&apos;identité</li>}
                  </ul>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setStep('lookup')}>Retour</Button>
                <Button
                  onClick={handleExecute}
                  disabled={loading || !confirmed || reason.trim().length < 10 || !targetUserId}
                  className={`${
                    mode === 'release_email'
                      ? 'bg-rose-700 hover:bg-rose-800 text-white'
                      : 'bg-rose-600 hover:bg-rose-700 text-white'
                  } disabled:opacity-40 disabled:cursor-not-allowed`}
                  title={
                    !targetUserId ? 'Sélectionnez un compte principal' :
                    reason.trim().length < 10 ? `Raison trop courte (${reason.trim().length}/10 caractères)` :
                    !confirmed ? 'Cochez la confirmation d\'identité' :
                    mode === 'release_email' ? 'Libérer définitivement l\'email' : 'Reconstruire l\'accès'
                  }
                  data-testid="recovery-execute-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  {mode === 'release_email' ? (
                    <><Trash2 className="w-4 h-4 mr-1" />Libérer définitivement l&apos;email</>
                  ) : (
                    <>Reconstruire l&apos;accès</>
                  )}
                </Button>
              </div>
            </div>
          )}
          {step === 'result' && (
            <Button onClick={onClose} data-testid="recovery-close-btn">Fermer</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
